// src/services/adminClubService.ts

import apiClient from "./apiClient";
import { ClubModel, CreateClubRequest, UpdateClubRequest } from "../models/ClubModel";
import { PageResponse } from "../models/PaginationModel";

// Admin-specific endpoint — has server-side filtering, sorting, and includes inactive clubs
const BASE_URL = "/api/admin/clubs";

export interface AdminClubFilters {
  search: string;
  country: string;        // "" = all, "TR" / "ES" / etc.
  active: "" | "true" | "false";
  locked?: "" | "true" | "false";
  sortBy: string;
  sortDir: "asc" | "desc";
}

/**
 * Get paginated, filtered list of clubs.
 * Server-side filtering — backend handles search, country, active, locked, sort.
 */
export const getAdminClubs = async (
  page: number,
  pageSize: number,
  filters: AdminClubFilters
): Promise<{ content: ClubModel[]; totalPages: number; totalItems: number }> => {
  // Build query string
  const params = new URLSearchParams({
    page: String(page),
    size: String(pageSize),
    sortBy: filters.sortBy || "shortName",
    sortDir: filters.sortDir || "asc",
  });

  if (filters.search.trim()) params.set("search", filters.search.trim());
  if (filters.country)       params.set("country", filters.country);
  if (filters.active !== "") params.set("active", filters.active);
  if (filters.locked) params.set("locked", filters.locked);

  const response = await apiClient.get<PageResponse<ClubModel>>(`${BASE_URL}?${params.toString()}`);

  return {
    content:    response.content ?? [],
    totalPages: response.totalPages ?? 1,
    totalItems: response.totalItems ?? response.content?.length ?? 0,
  };
};

/** Get single club by ID */
export const getAdminClubById = (id: number): Promise<ClubModel> =>
  apiClient.get<ClubModel>(`${BASE_URL}/${id}`);

/** Create a new club (ADMIN or MODERATOR) */
export const createAdminClub = (data: CreateClubRequest): Promise<ClubModel> =>
  apiClient.post<ClubModel>(BASE_URL, data);

/** Update an existing club (ADMIN or MODERATOR) */
export const updateAdminClub = (id: number, data: UpdateClubRequest): Promise<ClubModel> =>
  apiClient.put<ClubModel>(`${BASE_URL}/${id}`, data);

/** Hard-delete a club (ADMIN only). Blocked by backend if any team branch has match history. */
export const deleteAdminClub = (id: number): Promise<void> =>
  apiClient.delete<void>(`${BASE_URL}/${id}`);

/** Quick-toggle active flag — convenience wrapper for the table row toggle button */
export const toggleClubActive = (id: number, active: boolean): Promise<ClubModel> =>
  apiClient.put<ClubModel>(`${BASE_URL}/${id}`, { active });

/** Lock or unlock a club topic — blocks/allows new entry submissions (ADMIN or MODERATOR) */
export const setClubLocked = (id: number, locked: boolean, lockReason?: string, lockReasonVisible?: boolean): Promise<ClubModel> =>
  apiClient.patch<ClubModel>(`/api/admin/clubs/${id}/lock`, { locked, lockReason, lockReasonVisible });